"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PUBLICATION_ROW_RULE_ROLES = exports.DEFAULT_CONTENT_CLUSTER_CONTRACT = exports.ContentClusterContractSchema = exports.REQUIRED_PUBLICATION_COLUMN_KEYS = exports.PublicationColumnContractSchema = exports.PUBLICATION_COLUMN_KEYS = exports.PublicationColumnKeySchema = exports.NON_PUBLIC_PUBLICATION_STATUSES = exports.PUBLISHABLE_PUBLICATION_STATUSES = exports.PUBLICATION_STATUS_OPTION_IDS = exports.PUBLICATION_STATUS_OPTIONS = exports.PublicationTimingSchema = exports.PublicationStatusSchema = void 0;
exports.publicationTimeColumnIssue = publicationTimeColumnIssue;
exports.createPublicationColumnIndex = createPublicationColumnIndex;
exports.readPublicationColumnIndex = readPublicationColumnIndex;
exports.resolvePublicationStatusColumn = resolvePublicationStatusColumn;
exports.isPublicationStatus = isPublicationStatus;
exports.publicationRowRuleAppliesToSource = publicationRowRuleAppliesToSource;
exports.publicationRowRuleApplies = publicationRowRuleApplies;
exports.pageCollectionSourceKey = pageCollectionSourceKey;
exports.publicationPageSourceKeys = publicationPageSourceKeys;
exports.isPublicationRowDeliverable = isPublicationRowDeliverable;
exports.publicationRowDueAt = publicationRowDueAt;
const database_identity_1 = require("@streamlain/database-identity");
const zod_1 = require("zod");
const app_base_contract_1 = require("./app-base-contract");
exports.PublicationStatusSchema = zod_1.z.enum([
    'draft',
    'review',
    'approved',
    'scheduled',
    'published',
    'archived',
]);
const PublicationTimestampSchema = zod_1.z
    .string()
    .refine(value => Number.isFinite(Date.parse(value)), 'must be an ISO date or date-time');
exports.PublicationTimingSchema = zod_1.z
    .object({
    status: exports.PublicationStatusSchema,
    publishedAt: PublicationTimestampSchema.optional(),
    scheduledAt: PublicationTimestampSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.status === 'scheduled' && !value.scheduledAt) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['scheduledAt'],
            message: 'scheduled status requires scheduledAt',
        });
    }
    if (value.status !== 'scheduled' && value.scheduledAt) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['scheduledAt'],
            message: 'scheduledAt is only valid for scheduled status',
        });
    }
    if (value.status === 'published' && !value.publishedAt) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['publishedAt'],
            message: 'published status requires publishedAt',
        });
    }
    if (['draft', 'review', 'approved'].includes(value.status) &&
        value.publishedAt) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['publishedAt'],
            message: `${value.status} status cannot have publishedAt`,
        });
    }
});
exports.PUBLICATION_STATUS_OPTIONS = [
    { id: 'draft', value: 'draft', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Yellow },
    { id: 'review', value: 'review', color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Blue },
    {
        id: 'approved',
        value: 'approved',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Purple,
    },
    {
        id: 'scheduled',
        value: 'scheduled',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Teal,
    },
    {
        id: 'published',
        value: 'published',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Green,
    },
    {
        id: 'archived',
        value: 'archived',
        color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Grey,
    },
];
exports.PUBLICATION_STATUS_OPTION_IDS = exports.PUBLICATION_STATUS_OPTIONS.map(option => option.id);
exports.PUBLISHABLE_PUBLICATION_STATUSES = [
    'published',
    'scheduled',
];
exports.NON_PUBLIC_PUBLICATION_STATUSES = [
    'draft',
    'review',
    'approved',
    'archived',
];
exports.PublicationColumnKeySchema = zod_1.z.enum([
    'slug',
    'title',
    'description',
    'body',
    'image',
    'status',
    'publishedAt',
    'scheduledAt',
    'canonical',
    'hreflangGroup',
    'locale',
    'requiredLocale',
    'noindex',
    'clusterId',
    'topicId',
    'internalLinkRowIds',
]);
exports.PUBLICATION_COLUMN_KEYS = {
    SLUG: 'slug',
    TITLE: 'title',
    DESCRIPTION: 'description',
    BODY: 'body',
    IMAGE: 'image',
    STATUS: 'status',
    PUBLISHED_AT: 'publishedAt',
    SCHEDULED_AT: 'scheduledAt',
    CANONICAL: 'canonical',
    HREFLANG_GROUP: 'hreflangGroup',
    LOCALE: 'locale',
    REQUIRED_LOCALE: 'requiredLocale',
    NOINDEX: 'noindex',
    CLUSTER_ID: 'clusterId',
    TOPIC_ID: 'topicId',
    INTERNAL_LINK_ROW_IDS: 'internalLinkRowIds',
};
exports.PublicationColumnContractSchema = zod_1.z
    .object({
    /**
     * Database-local column id. The semantic API field is kept separately so
     * display-name changes can never alter publication behavior.
     */
    field: zod_1.z.string().min(1),
    semanticKey: exports.PublicationColumnKeySchema,
    relation: zod_1.z
        .object({
        targetSourceKey: zod_1.z.string().min(1),
        targetColumnKey: exports.PublicationColumnKeySchema,
    })
        .optional(),
    required: zod_1.z.boolean().optional(),
})
    .strict();
const PUBLICATION_TIME_COLUMN_KEYS = [
    exports.PUBLICATION_COLUMN_KEYS.PUBLISHED_AT,
    exports.PUBLICATION_COLUMN_KEYS.SCHEDULED_AT,
];
/** Column types that hold a time as milliseconds instead of text. */
const NON_TEXT_PUBLICATION_TIME_COLUMN_TYPES = [
    'date',
    'number',
];
/**
 * Why a column cannot carry a publication time, or null when it can.
 * Publication times are read as text and parsed as an ISO date; milliseconds
 * from a date or number column never parse, so the row would never go live.
 */
function publicationTimeColumnIssue(column, semanticKey) {
    if (column.type === undefined ||
        !PUBLICATION_TIME_COLUMN_KEYS.includes(semanticKey) ||
        !NON_TEXT_PUBLICATION_TIME_COLUMN_TYPES.includes(column.type)) {
        return null;
    }
    return `Publication binding "${semanticKey}" for column "${column.id}" must not be a ${column.type} column: use a text column and store the time as an ISO date or date-time`;
}
exports.REQUIRED_PUBLICATION_COLUMN_KEYS = [
    exports.PUBLICATION_COLUMN_KEYS.STATUS,
    exports.PUBLICATION_COLUMN_KEYS.SLUG,
    exports.PUBLICATION_COLUMN_KEYS.TITLE,
    exports.PUBLICATION_COLUMN_KEYS.DESCRIPTION,
];
function createPublicationColumnIndex(columns) {
    const index = {};
    for (const column of columns) {
        const publication = column.data?.['publication'];
        if (!publication || typeof publication !== 'object')
            continue;
        const parsed = exports.PublicationColumnContractSchema.safeParse(publication);
        if (!parsed.success) {
            throw new Error(`Publication binding for column "${column.id}" is invalid: ${parsed.error.issues
                .map(issue => issue.message)
                .join('; ')}`);
        }
        if (parsed.data.field !== column.id) {
            throw new Error(`Publication binding for column "${column.id}" must reference that column id`);
        }
        const semanticKey = parsed.data.semanticKey;
        const timeIssue = publicationTimeColumnIssue(column, semanticKey);
        if (timeIssue)
            throw new Error(timeIssue);
        const existing = index[semanticKey];
        if (existing) {
            throw new Error(`Duplicate publication binding "${semanticKey}" for columns "${existing.id}" and "${column.id}"`);
        }
        index[semanticKey] = column;
    }
    for (const semanticKey of exports.REQUIRED_PUBLICATION_COLUMN_KEYS) {
        if (!index[semanticKey]) {
            throw new Error(`Missing required publication binding "${semanticKey}"`);
        }
    }
    return index;
}
/**
 * Publication columns of a table that is read, not published row by row. A
 * table without any publication binding is no content table (the table behind
 * a form or a fixed-row page) and has none; a table that carries bindings
 * must carry the required ones.
 */
function readPublicationColumnIndex(columns) {
    const bound = columns.some(column => {
        const publication = column.data?.['publication'];
        return Boolean(publication) && typeof publication === 'object';
    });
    return bound ? createPublicationColumnIndex(columns) : {};
}
/**
 * The column a publication source delivers its rows by: the bound status
 * column when it is a select carrying every publication status. Without it the
 * source has no status rule.
 */
function resolvePublicationStatusColumn(index) {
    const column = index[exports.PUBLICATION_COLUMN_KEYS.STATUS];
    const options = column?.data?.['options'];
    if (!column || column.type !== 'select' || !Array.isArray(options))
        return null;
    const ids = new Set(options.map(option => option && typeof option === 'object'
        ? option['id']
        : null));
    return exports.PUBLICATION_STATUS_OPTION_IDS.every(id => ids.has(id)) ? column : null;
}
exports.ContentClusterContractSchema = zod_1.z
    .object({
    clusterSourceKey: zod_1.z.string().min(1),
    topicSourceKey: zod_1.z.string().min(1),
    contentSourceKey: zod_1.z.string().min(1),
    clusterIdColumnKey: zod_1.z.literal(exports.PUBLICATION_COLUMN_KEYS.CLUSTER_ID),
    topicIdColumnKey: zod_1.z.literal(exports.PUBLICATION_COLUMN_KEYS.TOPIC_ID),
    slugColumnKey: zod_1.z.literal(exports.PUBLICATION_COLUMN_KEYS.SLUG),
    titleColumnKey: zod_1.z.literal(exports.PUBLICATION_COLUMN_KEYS.TITLE),
    descriptionColumnKey: zod_1.z.literal(exports.PUBLICATION_COLUMN_KEYS.DESCRIPTION),
    internalLinksColumnKey: zod_1.z.literal(exports.PUBLICATION_COLUMN_KEYS.INTERNAL_LINK_ROW_IDS),
})
    .strict();
exports.DEFAULT_CONTENT_CLUSTER_CONTRACT = exports.ContentClusterContractSchema.parse({
    clusterSourceKey: 'clusters',
    topicSourceKey: 'topics',
    contentSourceKey: 'content',
    clusterIdColumnKey: exports.PUBLICATION_COLUMN_KEYS.CLUSTER_ID,
    topicIdColumnKey: exports.PUBLICATION_COLUMN_KEYS.TOPIC_ID,
    slugColumnKey: exports.PUBLICATION_COLUMN_KEYS.SLUG,
    titleColumnKey: exports.PUBLICATION_COLUMN_KEYS.TITLE,
    descriptionColumnKey: exports.PUBLICATION_COLUMN_KEYS.DESCRIPTION,
    internalLinksColumnKey: exports.PUBLICATION_COLUMN_KEYS.INTERNAL_LINK_ROW_IDS,
});
function isPublicationStatus(value) {
    return exports.PublicationStatusSchema.safeParse(value).success;
}
/** Source roles whose rows a publication delivers by their status. */
exports.PUBLICATION_ROW_RULE_ROLES = new Set([
    'content',
    'users',
    'clusters',
    'topics',
]);
/**
 * Whether the rows of a source are delivered by their publication status in
 * an App Container: one of the deliveries that serve the source is not live
 * to the workspace.
 */
function publicationRowRuleAppliesToSource(input) {
    return (0, app_base_contract_1.resolveAppDeliveriesForSource)(input.manifest, input.sourceKey).some(resolved => publicationRowRuleApplies({
        deliveryMode: resolved.delivery.mode,
        role: input.role,
    }));
}
/**
 * Whether the rows of a source are delivered by their publication status:
 * for the roles that hold publishable entries, in every App Container that
 * is not delivered live to its workspace.
 */
function publicationRowRuleApplies(input) {
    return (input.deliveryMode !== 'internal_live' &&
        exports.PUBLICATION_ROW_RULE_ROLES.has(input.role));
}
/**
 * The source whose rows a detail page shows: the page's source with the
 * collection role, else its first source.
 */
function pageCollectionSourceKey(page, roleOf) {
    if (page.collectionRole) {
        const roleSourceKey = page.dataSourceKeys.find(sourceKey => roleOf(sourceKey) === page.collectionRole);
        if (roleSourceKey)
            return roleSourceKey;
    }
    return page.dataSourceKeys[0] ?? null;
}
/**
 * Sources whose rows the App Container shows as pages of their own: the
 * `:slug` source of each route and the collection of each detail page. Only
 * their rows need the page fields (slug, title, description); every other
 * publication source needs a status alone.
 */
function publicationPageSourceKeys(input) {
    const keys = new Set();
    for (const route of input.routes ?? []) {
        if (route.dynamicSlugSourceKey)
            keys.add(route.dynamicSlugSourceKey);
    }
    for (const page of input.pages ?? []) {
        if (page.routeKind !== 'detail')
            continue;
        const key = pageCollectionSourceKey(page, input.roleOf ?? (() => undefined));
        if (key)
            keys.add(key);
    }
    return keys;
}
/**
 * The time of a scheduled row in milliseconds since the epoch: `scheduledAt`,
 * or `publishedAt` when no `scheduledAt` is set. Null for a row that is not
 * scheduled or names no readable time.
 */
function scheduledPublicationTime(input) {
    if (input.status !== 'scheduled')
        return null;
    const due = input.scheduledAt ?? input.publishedAt;
    if (!due)
        return null;
    const parsed = Date.parse(due);
    return Number.isFinite(parsed) ? parsed : null;
}
/**
 * Whether a row of a publication source is delivered at `now`: a published
 * row always, a scheduled row from its time on.
 */
function isPublicationRowDeliverable(input) {
    if (input.status === 'published')
        return true;
    const time = scheduledPublicationTime(input);
    return time !== null && time <= input.now;
}
/**
 * When a row that is not delivered at `now` starts to be delivered, in
 * milliseconds since the epoch: the time of a scheduled row that lies ahead.
 * Null for every row whose delivery does not change by time alone.
 */
function publicationRowDueAt(input) {
    const time = scheduledPublicationTime(input);
    return time !== null && time > input.now ? time : null;
}
